import { useQuery } from "@tanstack/react-query";

import { axios } from "@/client/libs/axios";

/**
 * ─────────────────────────────────────────────────────────────────────────────
 *  JOURNAL D'AUDIT (console admin) — services client
 * ─────────────────────────────────────────────────────────────────────────────
 *  API : GET /audit (filtres userId/action/resource/status/période + pagination)
 *       GET /audit/export/csv (téléchargement CSV).
 *  Chaque entrée répond à : QUI a fait QUOI, sur QUELLE ressource, QUAND.
 * ─────────────────────────────────────────────────────────────────────────────
 */

export type AuditLogEntry = {
  id: string;
  userId: string | null;
  user?: { id: string; name: string; email: string } | null;
  action: string;
  resource: string;
  resourceId: string | null;
  changes?: Record<string, unknown> | null;
  status: "success" | "failure" | "warning";
  ipAddress?: string | null;
  userAgent?: string | null;
  metadata?: Record<string, unknown> | null;
  createdAt: string;
};

export type AuditFilters = {
  userId?: string;
  action?: string;
  resource?: string;
  status?: "success" | "failure" | "warning";
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
};

const fetchAuditLogs = async (filters: AuditFilters) => {
  const response = await axios.get<{
    data: AuditLogEntry[];
    pagination: { page: number; limit: number; total: number; pages: number };
  }>("/audit", { params: filters });
  return response.data;
};

export const useAuditLogs = (filters: AuditFilters = {}) => {
  const query = useQuery({
    queryKey: ["audit", "logs", filters],
    queryFn: () => fetchAuditLogs(filters),
  });

  return {
    logs: query.data?.data ?? [],
    pagination: query.data?.pagination,
    loading: query.isPending,
    error: query.error,
  };
};

/** Export CSV — téléchargement direct (blob). */
export const useExportAudit = () => {
  return async (filters: AuditFilters) => {
    const response = await axios.get("/audit/export/csv", {
      params: filters,
      responseType: "blob",
    });
    const url = URL.createObjectURL(response.data);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `audit-${new Date().toISOString().slice(0, 10)}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  };
};
